import { useEffect, useMemo, useState } from 'react'
import {
  listSnapshottedPolls,
  loadSnapshots,
  recentYesDelta,
} from '../../utils/snapshots.js'
import { CATEGORIES, DISCLAIMER_TEXT } from '../../utils/constants.js'

const LEVEL_FILTERS = [
  { id: 'federal',   label: 'Federal',       palette: 'federal' },
  { id: 'state',     label: 'State',         palette: 'state' },
  { id: 'city',      label: 'City',          palette: 'city' },
  { id: 'candidate', label: 'Candidates',    palette: 'candidate' },
  { id: 'user',      label: 'User-proposed', palette: 'user' },
]
const ALL_LEVEL_IDS = LEVEL_FILTERS.map((l) => l.id)

const TYPE_COLORS = {
  federal:   'var(--forest)',
  state:     'var(--sage)',
  city:      'var(--stone)',
  candidate: 'var(--granite)',
  user:      'var(--wheat)',
}

// ============================================================
// Reusable chart primitives
// ============================================================

function Donut({ data, size = 160 }) {
  const total = data.reduce((s, d) => s + d.value, 0)
  const cx = size / 2
  const cy = size / 2
  const r = size / 2 - 12
  const stroke = 22
  const c = 2 * Math.PI * r
  let offset = 0

  if (!total) {
    return (
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--chalk)" strokeWidth={stroke} />
      </svg>
    )
  }

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--chalk)" strokeWidth={stroke} />
      {data.map((d, i) => {
        if (!d.value) return null
        const len = (d.value / total) * c
        const seg = (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={d.color}
            strokeWidth={stroke}
            strokeDasharray={`${len} ${c - len}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )
        offset += len
        return seg
      })}
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
            fontSize="20" fontWeight="600" fill="var(--grove)">
        {total}
      </text>
    </svg>
  )
}

// 7-day trend showing 3 series per day (Yes / No / Unsure).
function TrendBars({ days, width = 320, height = 150 }) {
  if (!days.length || !days.some((d) => d.yes + d.no + d.unsure > 0)) {
    return <div className="chart-empty">No vote activity in the last 7 days.</div>
  }
  const max = Math.max(1, ...days.map((d) => Math.max(d.yes, d.no, d.unsure)))
  const padX = 24
  const padY = 18
  const innerW = width - padX * 2
  const innerH = height - padY * 2
  const groupW = innerW / days.length
  const bw = Math.max(4, groupW * 0.22)  // slimmer for 3-bar group
  const gap = 2

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height}>
      <line
        x1={padX} x2={width - padX}
        y1={height - padY} y2={height - padY}
        stroke="var(--linen-border)"
      />
      {days.map((d, i) => {
        const x0 = padX + i * groupW + groupW / 2
        const yesH    = (d.yes    / max) * innerH
        const noH     = (d.no     / max) * innerH
        const unsureH = (d.unsure / max) * innerH
        const xYes    = x0 - bw * 1.5 - gap
        const xNo     = x0 - bw / 2
        const xUnsure = x0 + bw / 2 + gap
        return (
          <g key={i}>
            <rect x={xYes}    y={height - padY - yesH}    width={bw} height={yesH}    fill="var(--yes-bar)"    rx="2" />
            <rect x={xNo}     y={height - padY - noH}     width={bw} height={noH}     fill="var(--no-bar)"     rx="2" />
            <rect x={xUnsure} y={height - padY - unsureH} width={bw} height={unsureH} fill="var(--unsure-bar)" rx="2" />
            <text x={x0} y={height - 4} textAnchor="middle" fontSize="10" fill="var(--sage)">
              {d.label}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

function HBarChart({ rows, width = 320, height }) {
  if (!rows.length) return <div className="chart-empty">Nothing to show yet.</div>
  const rowH = 26
  const padX = 12
  const labelW = 120
  const max = Math.max(1, ...rows.map((r) => r.value))
  const h = height || rows.length * rowH + 12
  const innerW = width - padX * 2 - labelW

  return (
    <svg viewBox={`0 0 ${width} ${h}`} width="100%" height={h}>
      {rows.map((r, i) => {
        const y = i * rowH + 6
        const w = (r.value / max) * innerW
        return (
          <g key={r.label}>
            {r.tooltip && <title>{r.tooltip}</title>}
            <text x={padX} y={y + rowH / 2} dominantBaseline="central"
                  fontSize="12" fill="var(--grove)">
              {r.label}
            </text>
            <rect x={padX + labelW} y={y + 4} width={w} height={rowH - 12}
                  fill={r.color || 'var(--forest)'} rx="2" />
            <text x={padX + labelW + w + 6} y={y + rowH / 2} dominantBaseline="central"
                  fontSize="11" fill="var(--sage)">
              {r.value}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

// ============================================================
// Helpers
// ============================================================

// Categorize a free-form "choice" string into Yes / No / Unsure / other.
// Accepts legacy Aye/Nay forms so old vote events still bucket correctly.
function bucketChoice(choice) {
  const c = (choice || '').toLowerCase()
  if (c === 'yes' || c === 'aye') return 'yes'
  if (c === 'no'  || c === 'nay') return 'no'
  if (c.startsWith('uns') || c.startsWith('abst')) return 'unsure'
  return null
}

// Build empty 7-day buckets ending today.
function emptySevenDays() {
  const days = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today)
    d.setDate(today.getDate() - i)
    days.push({
      date: d,
      label: d.toLocaleDateString(undefined, { weekday: 'short' }).slice(0, 2),
      yes: 0, no: 0, unsure: 0,
    })
  }
  return days
}

// Drop each vote into its createdAt-day bucket.
function fillTrend(days, voteRecords, isCandidatePoll) {
  for (const v of voteRecords) {
    if (isCandidatePoll) continue  // candidate races don't fit Yes/No/Unsure
    const bucket = bucketChoice(v.choice)
    if (!bucket) continue
    const dt = new Date(v.createdAt * 1000)
    dt.setHours(0, 0, 0, 0)
    const day = days.find((d) => d.date.getTime() === dt.getTime())
    if (!day) continue
    day[bucket] += 1
  }
  return days
}

// Build a long-format CSV of the current Analytics filter view: one row
// per (poll, option) so bills and candidate races share a schema.
function buildAnalyticsCsv(items, tallies) {
  const header = [
    'poll_id', 'kind', 'level', 'jurisdiction', 'title', 'bill_id',
    'posted_at', 'ends_at', 'option', 'resident_votes', 'nonresident_votes',
  ]
  const rows = [header]
  for (const item of items) {
    const tally = tallies[item.id] || {}
    const options = item.kind === 'candidate'
      ? (item.candidates || []).map((c) => c.name)
      : (item.options || [])
    for (const opt of options) {
      const t = tally[opt] || { resident: 0, nonResident: 0 }
      rows.push([
        item.id,
        item.kind || 'bill',
        item.level || '',
        item.jurisdiction || '',
        item.title || '',
        item.billId || '',
        item.postedAt ? item.postedAt.toISOString() : '',
        item.endsAt ? item.endsAt.toISOString() : '',
        opt,
        t.resident || 0,
        t.nonResident || 0,
      ])
    }
  }
  return rows.map((r) => r.map(csvEscape).join(',')).join('\n')
}

function csvEscape(v) {
  const s = String(v ?? '')
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function downloadAnalyticsCsv(items, tallies) {
  const csv = buildAnalyticsCsv(items, tallies)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `verdant-analytics-${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function jurisdictionDisplay(j) {
  if (!j) return 'Unknown'
  if (j === 'US') return 'Federal'
  return j.replace(/^US-/, '')
}

// ============================================================
// Overview (all polls)
// ============================================================

function Overview({ items, tallies, voteIndex, userVotesCount, userPubkey }) {
  const stats = useMemo(() => {
    const now = Date.now()
    const activePolls = items.filter((i) => !i.endsAt || i.endsAt.getTime() > now).length
    const jurisdictions = new Set(items.map((i) => i.jurisdiction))

    let totalVotes = 0
    const byType = { federal: 0, state: 0, city: 0, candidate: 0, user: 0 }
    const byJurisdiction = {}
    const contested = []

    for (const item of items) {
      const t = tallies[item.id] || {}
      const sum = Object.values(t).reduce(
        (s, v) => s + (v.resident || 0) + (v.nonResident || 0), 0)
      totalVotes += sum

      const typeKey = item.kind === 'candidate' ? 'candidate' : item.level
      if (byType[typeKey] !== undefined) byType[typeKey] += sum

      byJurisdiction[item.jurisdiction] =
        (byJurisdiction[item.jurisdiction] || 0) + sum

      if (item.kind === 'bill' && sum > 0) {
        const yes = (t['Yes']?.resident || 0) + (t['Yes']?.nonResident || 0)
        const no  = (t['No']?.resident  || 0) + (t['No']?.nonResident  || 0)
        const closeness = yes + no > 0
          ? 1 - Math.abs(yes - no) / (yes + no)
          : 0
        contested.push({ item, total: sum, closeness })
      }
    }

    // 7-day trend across every bill vote, by actual createdAt.
    const trend = emptySevenDays()
    for (const pollId of Object.keys(voteIndex)) {
      const poll = items.find((p) => p.id === pollId)
      if (!poll) continue
      fillTrend(trend, Object.values(voteIndex[pollId]), poll.kind === 'candidate')
    }

    const topJurisdictions = Object.entries(byJurisdiction)
      .map(([j, v]) => ({ label: jurisdictionDisplay(j), value: v, color: 'var(--forest)' }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 6)

    const topContested = contested
      .sort((a, b) => b.closeness - a.closeness || b.total - a.total)
      .slice(0, 5)
      .map(({ item, total, closeness }) => {
        const fullTitle = item.title || item.billId || 'Untitled'
        const label = fullTitle.length > 40
          ? `${fullTitle.slice(0, 39)}…`
          : fullTitle
        return {
          label,
          tooltip: fullTitle,
          value: total,
          color: 'var(--no-bar)',
          meta: `${Math.round(closeness * 100)}% contested`,
        }
      })

    // Biggest movers: bills whose Yes% has shifted most since the prior
    // snapshot. Pulled from snapshots in localStorage so this works even
    // for polls no longer in the live feed.
    const biggestMovers = listSnapshottedPolls()
      .map((pollId) => {
        const snaps = loadSnapshots(pollId)
        const delta = recentYesDelta(snaps)
        if (delta === null) return null
        const item = items.find((p) => p.id === pollId)
        const label = item?.billId
          || item?.title?.slice(0, 28)
          || pollId.slice(0, 8) + '…'
        return { pollId, label, delta }
      })
      .filter((m) => m !== null && Math.abs(m.delta) > 0)
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
      .slice(0, 5)

    return {
      totalVotes, activePolls,
      jurisdictionCount: jurisdictions.size,
      byType, trend, topJurisdictions, topContested, biggestMovers,
    }
  }, [items, tallies, voteIndex])

  const donutData = [
    { label: 'Federal',    value: stats.byType.federal,   color: TYPE_COLORS.federal },
    { label: 'State',      value: stats.byType.state,     color: TYPE_COLORS.state },
    { label: 'City',       value: stats.byType.city,      color: TYPE_COLORS.city },
    { label: 'Candidates', value: stats.byType.candidate, color: TYPE_COLORS.candidate },
    { label: 'User',       value: stats.byType.user,      color: TYPE_COLORS.user },
  ]

  return (
    <>
      <div className="metric-grid">
        <div className="metric">
          <div className="metric-value">{stats.totalVotes.toLocaleString()}</div>
          <div className="metric-label">Total votes cast</div>
        </div>
        <div className="metric">
          <div className="metric-value">{stats.activePolls}</div>
          <div className="metric-label">Active polls</div>
        </div>
        <div className="metric">
          <div className="metric-value">{stats.jurisdictionCount}</div>
          <div className="metric-label">Jurisdictions</div>
        </div>
        {userPubkey ? (
          <div className="metric">
            <div className="metric-value">{userVotesCount}</div>
            <div className="metric-label">Your votes</div>
          </div>
        ) : (
          <div className="metric">
            <div className="metric-value" style={{ fontSize: '0.95rem', fontWeight: 500 }}>
              Sign in
            </div>
            <div className="metric-label">to track your votes</div>
          </div>
        )}
      </div>

      <div className="chart-grid">
        <div className="chart-card">
          <h3 className="chart-title">Votes by type</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Donut data={donutData} />
            <div className="chart-legend" style={{ flexDirection: 'column' }}>
              {donutData.map((d) => (
                <span key={d.label}>
                  <span className="legend-dot" style={{ background: d.color }} />
                  {d.label} <strong>{d.value}</strong>
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="chart-card">
          <h3 className="chart-title">7-day trend (Yes / No / Unsure)</h3>
          <TrendBars days={stats.trend} />
          <div className="chart-legend">
            <span><span className="legend-dot" style={{ background: 'var(--yes-bar)' }} />Yes</span>
            <span><span className="legend-dot" style={{ background: 'var(--no-bar)' }} />No</span>
            <span><span className="legend-dot" style={{ background: 'var(--unsure-bar)' }} />Unsure</span>
          </div>
        </div>

        <div className="chart-card">
          <h3 className="chart-title">Most active jurisdictions</h3>
          <HBarChart rows={stats.topJurisdictions} />
        </div>

        <div className="chart-card">
          <h3 className="chart-title">Most contested polls</h3>
          <HBarChart rows={stats.topContested} />
        </div>

        <div className="chart-card">
          <h3 className="chart-title">Biggest movers (Yes% shift)</h3>
          <MoversList rows={stats.biggestMovers} />
        </div>
      </div>
    </>
  )
}

// Compact list of biggest sentiment shifts. Each row shows a label and
// the signed delta with arrow + color coding.
function MoversList({ rows }) {
  if (!rows.length) {
    return (
      <div className="chart-empty">
        Need 2+ snapshots per poll. Come back tomorrow.
      </div>
    )
  }
  return (
    <ul className="movers-list">
      {rows.map((m) => {
        const rising = m.delta > 0
        const flat = m.delta === 0
        const color = flat
          ? 'var(--unsure-text)'
          : rising ? 'var(--yes-text)' : 'var(--no-text)'
        const arrow = flat ? '→' : rising ? '↑' : '↓'
        return (
          <li key={m.pollId} className="movers-row">
            <span className="movers-label" title={m.pollId}>{m.label}</span>
            <span className="movers-delta" style={{ color }}>
              {arrow} {rising ? '+' : ''}{m.delta}%
            </span>
          </li>
        )
      })}
    </ul>
  )
}

// ============================================================
// Per-poll detail
// ============================================================

function PollDetail({ poll, tally, votes, userPubkey }) {
  const isCandidate = poll.kind === 'candidate'
  const options = isCandidate
    ? poll.candidates.map((c) => c.name)
    : poll.options

  const snapshots = useMemo(() => loadSnapshots(poll.id), [poll.id])

  const stats = useMemo(() => {
    let total = 0, resident = 0, nonResident = 0
    const breakdown = options.map((opt) => {
      const t = tally?.[opt] || { resident: 0, nonResident: 0 }
      total += t.resident + t.nonResident
      resident += t.resident
      nonResident += t.nonResident
      return { label: opt, resident: t.resident, nonResident: t.nonResident }
    })

    // Voter jurisdictions (top 6)
    const byJurisdiction = {}
    const records = Object.values(votes || {})
    for (const v of records) {
      const key = v.jurisdiction || '—'
      byJurisdiction[key] = (byJurisdiction[key] || 0) + 1
    }
    const topJurisdictions = Object.entries(byJurisdiction)
      .map(([j, v]) => ({ label: jurisdictionDisplay(j), value: v, color: 'var(--forest)' }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 6)

    const trend = fillTrend(emptySevenDays(), records, isCandidate)

    const userVote = userPubkey && votes?.[userPubkey]?.choice

    return { total, resident, nonResident, breakdown, topJurisdictions, trend, userVote }
  }, [options, tally, votes, isCandidate, userPubkey])

  return (
    <>
      <div className="poll-detail-head">
        <div className="card-meta">
          <span className={`badge ${poll.level}`}>{poll.levelLabel}</span>
          {poll.billId && <span className="bill-id">{poll.billId}</span>}
          {poll.kind === 'candidate' && <span className="badge candidate">Candidate</span>}
        </div>
        <h2 className="poll-detail-title">
          {poll.title || poll.plainSummary || 'Untitled poll'}
        </h2>
      </div>

      <div className="metric-grid">
        <div className="metric">
          <div className="metric-value">{stats.total}</div>
          <div className="metric-label">Total votes</div>
        </div>
        <div className="metric">
          <div className="metric-value">{stats.resident}</div>
          <div className="metric-label">Resident</div>
        </div>
        <div className="metric">
          <div className="metric-value">{stats.nonResident}</div>
          <div className="metric-label">Non-resident</div>
        </div>
        <div className="metric">
          <div className="metric-value" style={{ fontSize: stats.userVote ? '1.1rem' : undefined }}>
            {stats.userVote || '—'}
          </div>
          <div className="metric-label">Your vote</div>
        </div>
      </div>

      <div className="chart-grid">
        <div className="chart-card">
          <h3 className="chart-title">Vote breakdown</h3>
          <BreakdownBars rows={stats.breakdown} total={stats.total} isCandidate={isCandidate} />
        </div>

        {!isCandidate && (
          <div className="chart-card">
            <h3 className="chart-title">7-day trend (Yes / No / Unsure)</h3>
            <TrendBars days={stats.trend} />
            <div className="chart-legend">
              <span><span className="legend-dot" style={{ background: 'var(--yes-bar)' }} />Yes</span>
              <span><span className="legend-dot" style={{ background: 'var(--no-bar)' }} />No</span>
              <span><span className="legend-dot" style={{ background: 'var(--unsure-bar)' }} />Unsure</span>
            </div>
          </div>
        )}

        <div className="chart-card">
          <h3 className="chart-title">Voter jurisdictions</h3>
          <HBarChart rows={stats.topJurisdictions} />
        </div>

        {!isCandidate && (
          <div className="chart-card" style={{ gridColumn: '1 / -1' }}>
            <SentimentOverTime snapshots={snapshots} />
          </div>
        )}
      </div>
    </>
  )
}

// Full snapshot history line chart for a single poll. Toggles between
// percentage (Yes% / No%) and absolute (Yes / No / Unsure counts).
function SentimentOverTime({ snapshots }) {
  const [mode, setMode] = useState('percent') // 'percent' | 'absolute'

  if (!snapshots.length) {
    return (
      <>
        <h3 className="chart-title">Sentiment over time</h3>
        <div className="chart-empty">No snapshots yet for this poll.</div>
      </>
    )
  }

  const series = mode === 'percent'
    ? [
        { key: 'yes_pct',    label: 'Yes %',    color: 'var(--yes-bar)' },
        { key: 'no_pct',     label: 'No %',     color: 'var(--no-bar)'  },
        { key: 'unsure_pct', label: 'Unsure %', color: 'var(--unsure-bar)', faint: true },
      ]
    : [
        { key: 'yes_total',     label: 'Yes',     color: 'var(--yes-bar)' },
        { key: 'no_total',      label: 'No',      color: 'var(--no-bar)'  },
        { key: 'unsure_total',  label: 'Unsure',  color: 'var(--unsure-bar)' },
      ]

  // Derive absolute totals (resident + nonresident) and unsure_pct, which
  // older snapshots don't carry. Stored snapshots only have yes_pct and
  // no_pct; we recompute unsure_pct off the resident counts to match.
  const enriched = snapshots.map((s) => {
    const residentTotal = (s.yes_resident || 0) + (s.no_resident || 0) + (s.unsure_resident || 0)
    return {
      ...s,
      yes_total:    (s.yes_resident    || 0) + (s.yes_nonresident    || 0),
      no_total:     (s.no_resident     || 0) + (s.no_nonresident     || 0),
      unsure_total: (s.unsure_resident || 0) + (s.unsure_nonresident || 0),
      unsure_pct: residentTotal > 0
        ? Math.round(((s.unsure_resident || 0) / residentTotal) * 100)
        : 0,
    }
  })

  const max = mode === 'percent'
    ? 100
    : Math.max(1, ...enriched.flatMap((s) => series.map((sr) => s[sr.key])))

  const width = 480
  const height = 180
  const padX = 32
  const padY = 18
  const innerW = width - padX * 2
  const innerH = height - padY * 2
  const stepX = enriched.length > 1 ? innerW / (enriched.length - 1) : 0

  const pointFor = (i, val) => ({
    x: padX + i * stepX,
    y: padY + innerH - (val / max) * innerH,
  })

  return (
    <>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 8, flexWrap: 'wrap', marginBottom: 8,
      }}>
        <h3 className="chart-title" style={{ marginBottom: 0 }}>Sentiment over time</h3>
        <div className="scope-toggle" style={{ alignSelf: 'auto' }}>
          <button
            type="button"
            className={mode === 'percent' ? 'active' : ''}
            onClick={() => setMode('percent')}
          >Percent</button>
          <button
            type="button"
            className={mode === 'absolute' ? 'active' : ''}
            onClick={() => setMode('absolute')}
          >Counts</button>
        </div>
      </div>

      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height}>
        {/* y-axis baseline */}
        <line
          x1={padX} x2={width - padX}
          y1={height - padY} y2={height - padY}
          stroke="var(--linen-border)"
        />
        {/* gridlines: 25/50/75/100 in percent, none in absolute */}
        {mode === 'percent' && [25, 50, 75].map((p) => {
          const y = padY + innerH - (p / 100) * innerH
          return (
            <g key={p}>
              <line x1={padX} x2={width - padX} y1={y} y2={y}
                    stroke="var(--linen-border)" strokeDasharray="3 3" opacity="0.6" />
              <text x={padX - 4} y={y + 3} fontSize="9" fill="var(--sage)" textAnchor="end">
                {p}%
              </text>
            </g>
          )
        })}

        {/* one line per series — faint flag dims unsure to keep yes/no primary */}
        {series.map((sr) => {
          const points = enriched.map((s, i) => pointFor(i, s[sr.key] || 0))
          const path = points
            .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
            .join(' ')
          const opacity = sr.faint ? 0.45 : 1
          return (
            <g key={sr.key} opacity={opacity}>
              <path d={path} fill="none" stroke={sr.color} strokeWidth="2" />
              {points.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r="3" fill={sr.color} />
              ))}
            </g>
          )
        })}

        {/* x-axis labels — first, middle, last */}
        {enriched.length > 0 && (
          <>
            <text x={padX} y={height - 4} fontSize="9" fill="var(--sage)">
              {new Date(enriched[0].snapshot_at).toLocaleDateString()}
            </text>
            <text x={width - padX} y={height - 4} fontSize="9" fill="var(--sage)" textAnchor="end">
              {new Date(enriched[enriched.length - 1].snapshot_at).toLocaleDateString()}
            </text>
          </>
        )}
      </svg>

      <div className="chart-legend">
        {series.map((sr) => (
          <span key={sr.key}>
            <span className="legend-dot" style={{ background: sr.color }} />{sr.label}
          </span>
        ))}
      </div>
    </>
  )
}

// Resident/non-resident split bars per option, used in the detail view.
function BreakdownBars({ rows, total, isCandidate }) {
  if (!total) return <div className="chart-empty">No votes yet on this poll.</div>
  return (
    <div className="vote-block" style={{ marginTop: 4 }}>
      {rows.map((r) => {
        const sum = r.resident + r.nonResident
        const residentWidth = total ? (r.resident / total) * 100 : 0
        const nonResidentWidth = total ? (r.nonResident / total) * 100 : 0
        const colorVar = colorFor(r.label, isCandidate)
        const nrColor = nrColorFor(r.label, isCandidate)
        return (
          <div className="vote-row" key={r.label}>
            <span style={{ fontSize: '0.88rem', fontWeight: 500 }}>{r.label}</span>
            <div className="bar-wrap">
              <div className="bar-track">
                <span className="bar-segment" style={{ width: `${residentWidth}%`, background: colorVar }} />
                <span className="bar-segment" style={{ width: `${nonResidentWidth}%`, background: nrColor }} />
              </div>
              <div className="bar-labels">
                <span><strong>{r.resident}</strong> resident</span>
                <span><strong>{r.nonResident}</strong> non-resident</span>
              </div>
            </div>
            <span className="vote-count">{sum}</span>
          </div>
        )
      })}
    </div>
  )
}

function colorFor(label, isCandidate) {
  if (isCandidate) return 'var(--granite)'
  const k = label.toLowerCase()
  if (k === 'yes' || k === 'aye') return 'var(--yes-bar)'
  if (k === 'no'  || k === 'nay') return 'var(--no-bar)'
  return 'var(--unsure-bar)'
}
function nrColorFor(label, isCandidate) {
  if (isCandidate) return 'var(--dusk)'
  const k = label.toLowerCase()
  if (k === 'yes' || k === 'aye') return 'var(--yes-nr-bar)'
  if (k === 'no'  || k === 'nay') return 'var(--no-nr-bar)'
  return '#C0B5A0'
}

// ============================================================
// AnalyticsPane (selector + dispatch)
// ============================================================

export function AnalyticsPane({ items, tallies, voteIndex, jurisdiction, userPubkey, initialPollId = '' }) {
  const [selectedPollId, setSelectedPollId] = useState(initialPollId)
  const [activeLevels, setActiveLevels] = useState(() => new Set(ALL_LEVEL_IDS))
  const [topic, setTopic] = useState('') // '' = all topics

  // When Home routes us here from a card's "Detailed metrics →" link,
  // initialPollId arrives populated — sync it into the local selector
  // state. Empty string means "show overview" so we honor that too.
  useEffect(() => {
    setSelectedPollId(initialPollId || '')
  }, [initialPollId])

  // Topic dropdown groups:
  //   1. canonical — the 32 Congress.gov Policy Areas
  //   2. other     — any tag actually on an event but not in the canonical
  //                  list (e.g. legacy "energy"/"healthcare" from the bot)
  const { canonicalTopics, otherTopics } = useMemo(() => {
    const canonicalSet = new Set(CATEGORIES.map((c) => c.toLowerCase()))
    const seen = new Set()
    const other = []
    for (const item of items) {
      for (const c of item.categories || []) {
        const key = c.toLowerCase()
        if (canonicalSet.has(key) || seen.has(key)) continue
        seen.add(key)
        other.push(c)
      }
    }
    return {
      canonicalTopics: CATEGORIES,             // already sorted by SPEC order
      otherTopics: other.sort((a, b) => a.localeCompare(b)),
    }
  }, [items])

  const filteredItems = useMemo(() => {
    const topicLower = topic.toLowerCase()
    return items.filter((item) => {
      const typeKey = item.kind === 'candidate' ? 'candidate' : item.level
      if (!activeLevels.has(typeKey)) return false
      if (topic) {
        const cats = (item.categories || []).map((c) => c.toLowerCase())
        if (!cats.includes(topicLower)) return false
      }
      return true
    })
  }, [items, activeLevels, topic])

  const sortedPolls = useMemo(
    () => filteredItems.slice().sort((a, b) => b.postedAt - a.postedAt),
    [filteredItems]
  )

  // If the currently-selected poll falls out of the filtered set, drop it
  // back to the overview rather than rendering a stale detail.
  useEffect(() => {
    if (selectedPollId && !filteredItems.find((p) => p.id === selectedPollId)) {
      setSelectedPollId('')
    }
  }, [filteredItems, selectedPollId])

  const selectedPoll = filteredItems.find((p) => p.id === selectedPollId) || null

  const toggleLevel = (id) => {
    setActiveLevels((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const userVotesCount = useMemo(() => {
    try {
      const raw = localStorage.getItem('verdant_votes')
      if (!raw) return 0
      return Object.keys(JSON.parse(raw)).length
    } catch { return 0 }
  }, [items])

  return (
    <section>
      <div className="feed-disclaimer" role="note">
        {DISCLAIMER_TEXT}
      </div>
      <div className="analytics-controls">
        <div className="analytics-controls-row">
          <span className="analytics-label">Levels</span>
          <div className="type-pills">
            {LEVEL_FILTERS.map((t) => {
              const on = activeLevels.has(t.id)
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`type-pill ${on ? `active ${t.palette}` : ''}`}
                  onClick={() => toggleLevel(t.id)}
                  aria-pressed={on}
                >
                  {t.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="analytics-controls-row">
          <label className="analytics-label" htmlFor="topic-filter">Topic</label>
          <select
            id="topic-filter"
            className="analytics-select"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
          >
            <option value="">All topics</option>
            <optgroup label="Policy areas (Congress.gov)">
              {canonicalTopics.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </optgroup>
            {otherTopics.length > 0 && (
              <optgroup label="Other tags in feed">
                {otherTopics.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        <div className="analytics-controls-row">
          <label className="analytics-label" htmlFor="poll-drill">Drill down</label>
          <select
            id="poll-drill"
            className="analytics-select"
            value={selectedPollId}
            onChange={(e) => setSelectedPollId(e.target.value)}
            disabled={sortedPolls.length === 0}
          >
            <option value="">All polls (overview)</option>
            {sortedPolls.map((p) => {
              const headline = p.title || p.plainSummary || p.id.slice(0, 8)
              const truncated = headline.length > 60 ? headline.slice(0, 60) + '…' : headline
              const tag = p.kind === 'candidate' ? 'CAND' : (p.level || '').toUpperCase().slice(0, 4)
              return (
                <option key={p.id} value={p.id}>
                  [{tag}] {truncated}
                </option>
              )
            })}
          </select>
        </div>

        <div className="analytics-summary">
          <span>
            {filteredItems.length} of {items.length} polls match
            {topic && <> · topic <strong>{topic}</strong></>}
            {activeLevels.size < ALL_LEVEL_IDS.length && (
              <> · {activeLevels.size} of {ALL_LEVEL_IDS.length} levels</>
            )}
          </span>
          {(activeLevels.size < ALL_LEVEL_IDS.length || topic || selectedPollId) && (
            <button
              type="button"
              className="analytics-reset"
              onClick={() => {
                setActiveLevels(new Set(ALL_LEVEL_IDS))
                setTopic('')
                setSelectedPollId('')
              }}
            >
              Reset filters ↺
            </button>
          )}
          <button
            type="button"
            className="analytics-reset"
            onClick={() => downloadAnalyticsCsv(filteredItems, tallies)}
            disabled={filteredItems.length === 0}
          >
            Download CSV ↓
          </button>
        </div>
      </div>

      {selectedPoll ? (
        <PollDetail
          poll={selectedPoll}
          tally={tallies[selectedPoll.id]}
          votes={voteIndex[selectedPoll.id]}
          userPubkey={userPubkey}
        />
      ) : (
        <Overview
          items={filteredItems}
          tallies={tallies}
          voteIndex={voteIndex}
          userVotesCount={userVotesCount}
          userPubkey={userPubkey}
        />
      )}

      <div className="note" style={{ marginTop: 16 }}>
        {jurisdiction
          ? `Resident split based on jurisdiction: ${jurisdiction.city || jurisdiction.state || 'United States'}.`
          : 'Set your jurisdiction to see resident-only breakdowns.'}
      </div>
    </section>
  )
}
